var _MediaHelper = require('../helpers/media.helper');
var MediaHelper = _MediaHelper.MediaHelper;

/**
 * AudioDeviceController
 *
 * Manages all audio-device detection, microphone monitoring, and warning
 * banners.  Designed to be composed into AgentSalesDashboardCtrl via the
 * $controller service so that both controllers share the same $scope:
 *
 *   $controller('AudioDeviceController', { $scope: $scope });
 *
 * The following functions are exposed on $scope so AgentSalesDashboardCtrl
 * can call them directly:
 *   $scope.startMicrophoneMonitor(forceRestart)
 *   $scope.stopMicrophoneMonitor(clearSignals)
 *   $scope.updateMicrophoneSignal(source, active)
 *   $scope.applyMicrophoneCheckResult(result)
 *   $scope.getMicrophoneAccessFailureMessage(reason)
 *   $scope.applySelectedAudioDevices(showErrors)
 *   $scope.syncAudioDevices(options)
 *   $scope.monitorMicrophonePermission()
 *
 * AgentSalesDashboardCtrl must expose $scope.turnOffSwitch after its own
 * init so that microphone-permission blocking can call it.
 */
angular.module('callX').controller('AudioDeviceController', [
  '$scope', '$rootScope', '$modal', 'toaster',
  function($scope, $rootScope, $modal, toaster) {

    // ─── Microphone Warning State ─────────────────────────────────────────────

    $scope.showMicrophoneWarningBanner = false;
    $scope.microphoneWarningMessage = '';
    $scope.microphoneWarningLevel = 'warning';

    var microphoneSignals = {
      permissionBlocked: false,
      accessUnavailable: false,
      trackUnavailable: false,
    };
    var microphoneAccessFailureReason = 'ok';
    var microphonePermissionBlockedMessage = "Your browser does not have access to the microphone, check the permissions. You can't go online.";
    var microphoneMonitorStream = null;
    var microphoneMonitorTrack = null;
    var microphoneMonitorPollInterval = null;
    var microphoneMonitorStarting = false;

    function isPermissionBlockedReason(reason) {
      return reason === 'permission-denied' || reason === 'permission-prompt';
    }

    function getMicrophoneAccessFailureMessage(reason) {
      if (isPermissionBlockedReason(reason)) {
        return microphonePermissionBlockedMessage;
      }
      switch (reason) {
        case 'device-unavailable':
          return 'Your selected microphone is unavailable. Check your audio device settings before going online.';
        case 'device-in-use':
          return 'Your microphone is busy or unavailable. Close other apps using it before going online.';
        case 'not-supported':
          return 'This browser cannot access the microphone. Use a supported browser before going online.';
        default:
          return 'Your browser cannot access the microphone. Check your browser and device settings before going online.';
      }
    }

    function refreshMicrophoneWarning() {
      var nextVisible = false;
      var nextLevel = 'warning';
      var nextMessage = '';

      if (microphoneSignals.permissionBlocked) {
        nextVisible = true;
        nextLevel = 'blocking';
        nextMessage = microphonePermissionBlockedMessage;
      } else if (microphoneSignals.accessUnavailable) {
        nextVisible = true;
        nextLevel = 'blocking';
        nextMessage = getMicrophoneAccessFailureMessage(microphoneAccessFailureReason);
      } else if (microphoneSignals.trackUnavailable) {
        nextVisible = true;
        nextLevel = 'warning';
        nextMessage = 'Your microphone is unavailable. Check your browser or headset microphone settings.';
      }

      if (
        $scope.showMicrophoneWarningBanner !== nextVisible ||
        $scope.microphoneWarningLevel !== nextLevel ||
        $scope.microphoneWarningMessage !== nextMessage
      ) {
        $scope.showMicrophoneWarningBanner = nextVisible;
        $scope.microphoneWarningLevel = nextLevel;
        $scope.microphoneWarningMessage = nextMessage;
        $scope.$applyAsync();
      }
    }

    function updateMicrophoneSignal(source, active) {
      if (microphoneSignals[source] === active) {
        return;
      }
      microphoneSignals[source] = active;
      refreshMicrophoneWarning();
    }

    function applyMicrophoneCheckResult(result) {
      microphoneAccessFailureReason = result.reason;
      if (result.ok) {
        updateMicrophoneSignal('permissionBlocked', false);
        updateMicrophoneSignal('accessUnavailable', false);
        return;
      }
      var permissionBlocked = isPermissionBlockedReason(result.reason);
      updateMicrophoneSignal('permissionBlocked', permissionBlocked);
      updateMicrophoneSignal('accessUnavailable', !permissionBlocked);
    }

    function clearTrackedMicrophoneSignals() {
      updateMicrophoneSignal('trackUnavailable', false);
    }

    function evaluateMonitoredTrackState() {
      if (!microphoneMonitorTrack) {
        clearTrackedMicrophoneSignals();
        return;
      }
      var trackUnavailable = microphoneMonitorTrack.readyState === 'ended';
      updateMicrophoneSignal('trackUnavailable', trackUnavailable);
    }

    function bindMicrophoneMonitorTrack(track) {
      track.onmute = null;
      track.onunmute = null;
      track.onended = function() {
        evaluateMonitoredTrackState();
      };
      evaluateMonitoredTrackState();
    }

    function getAudioConstraintsForDeviceId(deviceId) {
      if (deviceId && deviceId !== 'default') {
        return { deviceId: { exact: deviceId } };
      }
      return true;
    }

    function stopMicrophoneMonitor(clearSignals) {
      if (microphoneMonitorPollInterval) {
        clearInterval(microphoneMonitorPollInterval);
        microphoneMonitorPollInterval = null;
      }
      if (microphoneMonitorTrack) {
        microphoneMonitorTrack.onmute = null;
        microphoneMonitorTrack.onunmute = null;
        microphoneMonitorTrack.onended = null;
      }
      if (microphoneMonitorStream) {
        try {
          microphoneMonitorStream.getTracks().forEach(function(track) { track.stop(); });
        } catch (_) {}
        microphoneMonitorStream = null;
      }
      microphoneMonitorTrack = null;
      if (clearSignals !== false) {
        clearTrackedMicrophoneSignals();
      }
    }

    function startMicrophoneMonitor(forceRestart) {
      if (!$scope.switchStatus || $scope.duplicateTab) {
        stopMicrophoneMonitor(true);
        return;
      }
      if (microphoneMonitorStarting) {
        return;
      }
      if (!forceRestart && microphoneMonitorStream && microphoneMonitorTrack && microphoneMonitorTrack.readyState === 'live') {
        evaluateMonitoredTrackState();
        return;
      }
      if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
        applyMicrophoneCheckResult({ ok: false, reason: 'not-supported' });
        return;
      }
      microphoneMonitorStarting = true;
      stopMicrophoneMonitor(true);
      var audioConstraints = getAudioConstraintsForDeviceId($scope.selectedAudioInputDeviceId);
      navigator.mediaDevices.getUserMedia({ audio: audioConstraints, video: false })
        .then(function(stream) {
          microphoneMonitorStream = stream;
          microphoneMonitorTrack = stream.getAudioTracks().length ? stream.getAudioTracks()[0] : null;
          applyMicrophoneCheckResult({ ok: true, reason: 'ok' });
          if (!microphoneMonitorTrack) {
            updateMicrophoneSignal('trackUnavailable', true);
            return;
          }
          bindMicrophoneMonitorTrack(microphoneMonitorTrack);
          microphoneMonitorPollInterval = window.setInterval(function() {
            evaluateMonitoredTrackState();
          }, 1000);
        })
        .catch(function(error) {
          var result = MediaHelper.classifyMicrophoneError(error);
          applyMicrophoneCheckResult(result);
          clearTrackedMicrophoneSignals();
          if ($scope.switchStatus && isPermissionBlockedReason(result.reason)) {
            if ($scope.turnOffSwitch) {
              $scope.turnOffSwitch();
            }
          }
        })
        .then(function() {
          microphoneMonitorStarting = false;
        });
    }

    // ─── Audio Device Management ──────────────────────────────────────────────

    $scope.audioDeviceMenuOpen = false;
    $scope.audioDevicesLoading = false;
    $scope.audioInputDevices = [];
    $scope.audioOutputDevices = [];
    $scope.selectedAudioInputDeviceId = '';
    $scope.selectedAudioOutputDeviceId = '';
    $scope.pendingAudioInputDeviceId = '';
    $scope.pendingAudioOutputDeviceId = '';
    $scope.audioInputDropdownOpen = false;
    $scope.audioOutputDropdownOpen = false;
    $scope.audioOutputSelectionSupported = false;
    $scope.audioInputTestActive = false;
    $scope.audioInputTestLevel = 0;
    $scope.audioOutputTestPlaying = false;
    $scope.audioTestMeterBars = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    $scope.audioDeviceDropdownStyle = {};
    $scope.showAudioDeviceChangedWarning = false;
    $scope.audioDeviceWarningType = '';
    $scope.audioDeviceWarningLabel = '';
    $scope.audioDeviceWarningMessage = '';
    $scope.audioInputSelectionMode = 'auto';
    $scope.audioOutputSelectionMode = 'auto';
    $scope.activeAudioInputDeviceId = '';
    $scope.activeAudioInputDeviceLabel = '';
    $scope.activeAudioOutputDeviceLabel = '';

    var audioDeviceChangeHandler = null;
    var audioDeviceChangeDebounce = null;
    var plivoAudioDeviceChangeHandler = null;
    var combinedAudioDeviceChangeHandler = null;
    var audioDeviceSyncChain = Promise.resolve();
    var audioInputTestContext = null;
    var audioInputTestAnalyser = null;
    var audioInputTestStream = null;
    var audioInputTestAnimationFrame = null;
    var audioOutputTestElement = null;
    var audioInputTestDeviceId = '';
    var audioOutputTestDeviceId = '';
    var audioInputTestStartToken = 0;

    function supportsAudioOutputSelection() {
      try {
        var proto = window.HTMLMediaElement && window.HTMLMediaElement.prototype;
        return !!(proto && typeof proto.setSinkId === 'function');
      } catch (_) {
        return false;
      }
    }

    function getAudioDeviceLabel(device, index) {
      if (device.deviceId === 'default') {
        return (device.label && device.label.trim()) || (device.kind === 'audioinput' ? 'System Default Microphone' : 'System Default Speaker');
      }
      if (device.label && device.label.trim()) {
        return device.label;
      }
      return device.kind === 'audioinput' ? ('Microphone ' + (index + 1)) : ('Speaker ' + (index + 1));
    }

    function normalizeAudioDeviceLabel(label) {
      return (label || '').replace(/\s+/g, ' ').trim().toLowerCase();
    }

    function buildUniqueAudioDeviceList(devices, kind) {
      var uniqueDevices = [];
      var seenKeys = {};
      var physicalIndex = 0;
      var hasDefault = false;
      devices
        .filter(function(d) {
          return d.kind === kind && d.deviceId && d.deviceId !== 'communications';
        })
        .forEach(function(d) {
          if (d.deviceId === 'default') {
            if (hasDefault) {
              return;
            }
            hasDefault = true;
            uniqueDevices.unshift({
              deviceId: d.deviceId,
              label: getAudioDeviceLabel(d, 0),
              kind: kind,
              groupId: d.groupId || '',
              isDefault: true,
            });
            return;
          }
          var rawLabelKey = normalizeAudioDeviceLabel(d.label || '');
          var uniqueKey = kind + '|' + (d.groupId || '') + '|' + (rawLabelKey || d.deviceId);
          if (seenKeys[uniqueKey]) {
            return;
          }
          seenKeys[uniqueKey] = true;
          uniqueDevices.push({
            deviceId: d.deviceId,
            label: getAudioDeviceLabel(d, physicalIndex),
            kind: kind,
            groupId: d.groupId || '',
            isDefault: false,
          });
          physicalIndex++;
        });
      return uniqueDevices;
    }

    function getDeviceById(devices, deviceId) {
      for (var i = 0; i < devices.length; i++) {
        if (devices[i].deviceId === deviceId) {
          return devices[i];
        }
      }
      return null;
    }

    function getDefaultDevice(devices) {
      return getDeviceById(devices, 'default');
    }

    function getFirstPhysicalDevice(devices) {
      for (var i = 0; i < devices.length; i++) {
        if (devices[i].deviceId !== 'default') {
          return devices[i];
        }
      }
      return null;
    }

    function getAudioDeviceListKey(devices) {
      return devices.map(function(d) {
        return [d.kind, d.deviceId, d.label, d.groupId].join('|');
      }).sort().join(',');
    }

    function getAudioDeviceLabelById(devices, deviceId, fallback) {
      var device = getDeviceById(devices, deviceId);
      return device ? device.label : fallback;
    }

    function resolveAudioDeviceSelection(devices, currentDeviceId, selectionMode, kind, activeInputProbe) {
      var mode = selectionMode === 'manual' ? 'manual' : 'auto';
      var reason = '';

      if (mode === 'manual') {
        if (currentDeviceId && getDeviceById(devices, currentDeviceId)) {
          return { deviceId: currentDeviceId, mode: 'manual', reason: '' };
        }
        mode = 'auto';
        reason = kind === 'audioinput' ? 'input-removed' : 'output-removed';
      }

      var defaultDevice = getDefaultDevice(devices);
      if (defaultDevice) {
        return { deviceId: defaultDevice.deviceId, mode: 'auto', reason: reason };
      }

      if (
        kind === 'audioinput' &&
        activeInputProbe &&
        activeInputProbe.ok &&
        activeInputProbe.deviceId &&
        getDeviceById(devices, activeInputProbe.deviceId)
      ) {
        return { deviceId: activeInputProbe.deviceId, mode: 'auto', reason: reason };
      }

      var firstPhysicalDevice = getFirstPhysicalDevice(devices);
      return {
        deviceId: firstPhysicalDevice ? firstPhysicalDevice.deviceId : 'default',
        mode: 'auto',
        reason: reason,
      };
    }

    function showAudioDeviceWarning(message, label) {
      $scope.audioDeviceWarningType = 'changed';
      $scope.audioDeviceWarningMessage = message || 'Audio device changed. Please check your audio settings.';
      $scope.audioDeviceWarningLabel = label || '';
      $scope.showAudioDeviceChangedWarning = true;
    }

    function getSelectedAudioDeviceSummary(inputDevices, outputDevices) {
      var inputLabel = getAudioDeviceLabelById(inputDevices, $scope.selectedAudioInputDeviceId, 'System Default Microphone');
      var outputLabel = getAudioDeviceLabelById(outputDevices, $scope.selectedAudioOutputDeviceId, 'System Default Speaker');
      if ($scope.audioOutputSelectionSupported && outputDevices.length) {
        return inputLabel + ' and ' + outputLabel;
      }
      return inputLabel;
    }

    function buildAudioDeviceWarningMessage(inputResolution, outputResolution, inputDevices, outputDevices, options, inputListChanged, outputListChanged) {
      var inputRemoved = inputResolution.reason === 'input-removed';
      var outputRemoved = outputResolution.reason === 'output-removed';
      if (inputRemoved && outputRemoved) {
        return 'Selected audio devices were disconnected. Using system default microphone and speaker.';
      }
      if (inputRemoved) {
        return 'Selected microphone was disconnected. Using ' + getAudioDeviceLabelById(inputDevices, inputResolution.deviceId, 'System Default Microphone') + '.';
      }
      if (outputRemoved) {
        return 'Selected speaker was disconnected. Using ' + getAudioDeviceLabelById(outputDevices, outputResolution.deviceId, 'System Default Speaker') + '.';
      }
      if (options && options.source === 'devicechange' && (inputListChanged || outputListChanged || $scope.audioInputSelectionMode === 'auto' || $scope.audioOutputSelectionMode === 'auto')) {
        return 'Audio device changed. Using ' + getSelectedAudioDeviceSummary(inputDevices, outputDevices) + '.';
      }
      return '';
    }

    async function shouldProbeSystemDefaultInput(forcePermission) {
      if (forcePermission || $scope.switchStatus) {
        return true;
      }
      try {
        if (navigator && navigator.permissions && navigator.permissions.query) {
          var permissionStatus = await navigator.permissions.query({ name: 'microphone' });
          return permissionStatus.state === 'granted';
        }
      } catch (_) {}
      return false;
    }

    function updateActiveAudioInputFromProbe(probe, inputDevices) {
      if (probe && probe.ok) {
        $scope.activeAudioInputDeviceId = probe.deviceId || '';
        $scope.activeAudioInputDeviceLabel = getAudioDeviceLabelById(inputDevices, probe.deviceId, probe.label || 'System Default Microphone');
      } else {
        $scope.activeAudioInputDeviceId = '';
        $scope.activeAudioInputDeviceLabel = getAudioDeviceLabelById(inputDevices, 'default', 'System Default Microphone');
      }
    }

    function updateActiveAudioOutputLabel(outputDevices) {
      $scope.activeAudioOutputDeviceLabel = getAudioDeviceLabelById(outputDevices, 'default', 'System Default Speaker');
    }

    function getPlivoAudio() {
      return $rootScope.plivoWebSdk &&
        $rootScope.plivoWebSdk.client &&
        $rootScope.plivoWebSdk.client.audio;
    }

    function applyAudioOutputSinkToRemoteAudio(outputDeviceId) {
      if (!outputDeviceId || !supportsAudioOutputSelection()) {
        return;
      }
      try {
        var remoteAudio = document.getElementById('plivo_webrtc_remoteview');
        if (remoteAudio && remoteAudio.setSinkId) {
          var sinkId = outputDeviceId === 'default' ? '' : outputDeviceId;
          remoteAudio.setSinkId(sinkId).catch(function(error) {
            console.error('Failed to route Plivo remote audio output', error);
          });
        }
      } catch (error) {
        console.error('Failed to apply Plivo remote audio sink', error);
      }
    }

    function applyAudioDevices(inputDeviceId, outputDeviceId, showErrors) {
      try {
        var audio = getPlivoAudio();
        if (audio) {
          if (inputDeviceId && audio.microphoneDevices) {
            var inputApplied = audio.microphoneDevices.set(inputDeviceId);
            if (inputApplied === false && showErrors) {
              toaster.pop('error', 'Microphone Unavailable', 'The selected microphone is no longer available.');
            }
          }
          if (outputDeviceId) {
            if (!supportsAudioOutputSelection()) {
              if (showErrors) {
                toaster.pop('warning', 'Speaker Selection Unsupported', 'This browser does not allow switching the audio output device.');
              }
            } else {
              if (audio.speakerDevices) {
                var outputApplied = outputDeviceId === 'default' && audio.speakerDevices.reset
                  ? audio.speakerDevices.reset()
                  : audio.speakerDevices.set(outputDeviceId);
                if (outputApplied === false && showErrors) {
                  toaster.pop('error', 'Speaker Unavailable', 'The selected speaker is no longer available.');
                }
              }
              if (audio.ringtoneDevices) {
                if (outputDeviceId === 'default' && audio.ringtoneDevices.reset) {
                  audio.ringtoneDevices.reset();
                } else {
                  audio.ringtoneDevices.set(outputDeviceId);
                }
              }
            }
          }
        }
        applyAudioOutputSinkToRemoteAudio(outputDeviceId);
      } catch (error) {
        console.error('Failed to apply selected audio devices', error);
        if (showErrors) {
          toaster.pop('error', 'Audio Device Error', 'The selected audio device could not be applied.');
        }
      }
    }

    function applySelectedAudioDevices(showErrors) {
      applyAudioDevices($scope.selectedAudioInputDeviceId, $scope.selectedAudioOutputDeviceId, showErrors);
    }

    async function runAudioDeviceSync(options) {
      options = options || {};
      if (!(navigator.mediaDevices && navigator.mediaDevices.enumerateDevices)) {
        $scope.audioInputDevices = [];
        $scope.audioOutputDevices = [];
        $scope.audioOutputSelectionSupported = false;
        return;
      }

      var showLoading = options.showLoading !== false;
      if (showLoading) {
        $scope.audioDevicesLoading = true;
        $scope.$applyAsync();
      }

      var previousInputId = $scope.selectedAudioInputDeviceId;
      var previousOutputId = $scope.selectedAudioOutputDeviceId;
      var previousInputListKey = getAudioDeviceListKey($scope.audioInputDevices || []);
      var previousOutputListKey = getAudioDeviceListKey($scope.audioOutputDevices || []);

      try {
        if (options.forcePermission && navigator.mediaDevices.getUserMedia) {
          try {
            var permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
            permissionStream.getTracks().forEach(function(track) { track.stop(); });
          } catch (_) {}
        }

        var devices = await navigator.mediaDevices.enumerateDevices();
        var inputDevices = buildUniqueAudioDeviceList(devices, 'audioinput');
        var outputDevices = buildUniqueAudioDeviceList(devices, 'audiooutput');
        var activeInputProbe = null;

        if (await shouldProbeSystemDefaultInput(!!options.forcePermission)) {
          activeInputProbe = await MediaHelper.getSystemDefaultAudioInput();
        }

        var inputResolution = resolveAudioDeviceSelection(
          inputDevices,
          $scope.selectedAudioInputDeviceId,
          $scope.audioInputSelectionMode,
          'audioinput',
          activeInputProbe
        );
        var outputResolution = resolveAudioDeviceSelection(
          outputDevices,
          $scope.selectedAudioOutputDeviceId,
          $scope.audioOutputSelectionMode,
          'audiooutput',
          null
        );

        var inputListKey = getAudioDeviceListKey(inputDevices);
        var outputListKey = getAudioDeviceListKey(outputDevices);
        var inputListChanged = previousInputListKey !== inputListKey;
        var outputListChanged = previousOutputListKey !== outputListKey;

        $scope.audioOutputSelectionSupported = supportsAudioOutputSelection();
        $scope.audioInputDevices = inputDevices;
        $scope.audioOutputDevices = outputDevices;
        $scope.audioInputSelectionMode = inputResolution.mode;
        $scope.audioOutputSelectionMode = outputResolution.mode;
        $scope.selectedAudioInputDeviceId = inputResolution.deviceId;
        $scope.selectedAudioOutputDeviceId = outputResolution.deviceId;
        $scope.pendingAudioInputDeviceId = inputResolution.deviceId;
        $scope.pendingAudioOutputDeviceId = outputResolution.deviceId;
        updateActiveAudioInputFromProbe(activeInputProbe, inputDevices);
        updateActiveAudioOutputLabel(outputDevices);
        closeAudioDeviceDropdowns();

        var selectedInputChanged = previousInputId !== $scope.selectedAudioInputDeviceId;
        var selectedOutputChanged = previousOutputId !== $scope.selectedAudioOutputDeviceId;

        if (options.source === 'devicechange') {
          if (inputResolution.mode === 'manual' || outputResolution.mode === 'manual') {
            applyAudioDevices(
              inputResolution.mode === 'manual' ? $scope.selectedAudioInputDeviceId : '',
              outputResolution.mode === 'manual' ? $scope.selectedAudioOutputDeviceId : '',
              false
            );
          }
        } else if (options.applyToPlivo || options.forceApply) {
          applySelectedAudioDevices(false);
        }

        if (
          $scope.selectedAudioInputDeviceId &&
          $scope._realtimeAudioCtx &&
          $scope._realtimeMerger &&
          options.updateRealtime !== false &&
          (selectedInputChanged || options.forceRealtimeUpdate || (options.source === 'devicechange' && $scope.audioInputSelectionMode === 'auto'))
        ) {
          swapDeepgramMicSource($scope.selectedAudioInputDeviceId);
        }

        if ($scope.selectedAudioInputDeviceId && $scope.switchStatus && options.restartMonitor !== false) {
          startMicrophoneMonitor(true);
        }

        if (options.showWarnings) {
          var warningMessage = buildAudioDeviceWarningMessage(inputResolution, outputResolution, inputDevices, outputDevices, options, inputListChanged, outputListChanged);
          if (warningMessage) {
            showAudioDeviceWarning(warningMessage, getSelectedAudioDeviceSummary(inputDevices, outputDevices));
          }
        }

        if (options.source !== 'devicechange' && selectedOutputChanged && $scope.selectedAudioOutputDeviceId) {
          applyAudioOutputSinkToRemoteAudio($scope.selectedAudioOutputDeviceId);
        }
      } catch (error) {
        console.error('Failed to refresh audio devices', error);
      } finally {
        if (showLoading) {
          $scope.audioDevicesLoading = false;
        }
        $scope.$applyAsync();
      }
    }

    function syncAudioDevices(options) {
      audioDeviceSyncChain = audioDeviceSyncChain.then(function() {
        return runAudioDeviceSync(options || {});
      }).catch(function(error) {
        console.error('Failed to sync audio devices', error);
      });
      return audioDeviceSyncChain;
    }

    function closeAudioDeviceDropdowns() {
      $scope.audioInputDropdownOpen = false;
      $scope.audioOutputDropdownOpen = false;
    }

    function stopAudioInputDeviceTest() {
      audioInputTestStartToken++;
      $scope.audioInputTestActive = false;
      $scope.audioInputTestLevel = 0;
      audioInputTestDeviceId = '';
      if (audioInputTestAnimationFrame !== null) {
        cancelAnimationFrame(audioInputTestAnimationFrame);
        audioInputTestAnimationFrame = null;
      }
      if (audioInputTestStream) {
        audioInputTestStream.getTracks().forEach(function(track) { track.stop(); });
        audioInputTestStream = null;
      }
      if (audioInputTestAnalyser) {
        try {
          audioInputTestAnalyser.disconnect();
        } catch (_) {}
        audioInputTestAnalyser = null;
      }
      if (audioInputTestContext) {
        var contextToClose = audioInputTestContext;
        audioInputTestContext = null;
        contextToClose.close().catch(function() {});
      }
      $scope.$applyAsync();
    }

    async function startAudioInputDeviceTest(inputDeviceId, outputDeviceId, showErrors) {
      var resolvedInputDeviceId = inputDeviceId || 'default';
      var resolvedOutputDeviceId = outputDeviceId || $scope.pendingAudioOutputDeviceId || 'default';
      if ($scope.audioInputTestActive && audioInputTestDeviceId === resolvedInputDeviceId) {
        return;
      }
      if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
        if (showErrors) {
          toaster.pop('error', 'Microphone Test Unavailable', 'This browser does not support microphone device testing.');
        }
        return;
      }

      stopAudioInputDeviceTest();
      applyAudioDevices(resolvedInputDeviceId, resolvedOutputDeviceId, !!showErrors);
      var startToken = ++audioInputTestStartToken;
      try {
        var audioConstraints = getAudioConstraintsForDeviceId(resolvedInputDeviceId);
        var nextStream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
          video: false,
        });
        if (startToken !== audioInputTestStartToken) {
          nextStream.getTracks().forEach(function(track) { track.stop(); });
          return;
        }

        audioInputTestStream = nextStream;
        audioInputTestContext = new (window.AudioContext || window.webkitAudioContext)();
        var source = audioInputTestContext.createMediaStreamSource(audioInputTestStream);
        audioInputTestAnalyser = audioInputTestContext.createAnalyser();
        audioInputTestAnalyser.fftSize = 256;
        source.connect(audioInputTestAnalyser);

        var bufferLength = audioInputTestAnalyser.frequencyBinCount;
        var dataArray = new Uint8Array(bufferLength);

        $scope.audioInputTestActive = true;
        $scope.audioInputTestLevel = 0;
        audioInputTestDeviceId = resolvedInputDeviceId;
        $scope.$applyAsync();

        var updateInputLevel = function() {
          if (!$scope.audioInputTestActive || !audioInputTestAnalyser || startToken !== audioInputTestStartToken) {
            return;
          }
          audioInputTestAnalyser.getByteFrequencyData(dataArray);
          var total = 0;
          for (var index = 0; index < bufferLength; index++) {
            total += dataArray[index];
          }
          var average = total / bufferLength;
          var mappedLevel = Math.min($scope.audioTestMeterBars.length, Math.max(0, Math.round(average / 12)));
          $scope.audioInputTestLevel = mappedLevel;
          $scope.$applyAsync();
          audioInputTestAnimationFrame = requestAnimationFrame(updateInputLevel);
        };

        updateInputLevel();
      } catch (error) {
        console.error('Failed to test selected microphone device', error);
        stopAudioInputDeviceTest();
        if (showErrors) {
          toaster.pop('error', 'Microphone Test Failed', 'The selected microphone could not be opened for testing.');
        }
      }
    }

    function startAudioInputDeviceTestIfMenuOpen(showErrors) {
      if (!$scope.audioDeviceMenuOpen || !$scope.audioInputDevices.length) {
        return Promise.resolve();
      }
      return startAudioInputDeviceTest($scope.pendingAudioInputDeviceId, $scope.pendingAudioOutputDeviceId, showErrors);
    }

    function stopAudioOutputDeviceTest() {
      if (audioOutputTestElement) {
        audioOutputTestElement.pause();
        audioOutputTestElement.currentTime = 0;
      }
      audioOutputTestDeviceId = '';
      $scope.audioOutputTestPlaying = false;
      $scope.$applyAsync();
    }

    $scope.closeAudioDeviceMenu = function() {
      stopAudioInputDeviceTest();
      stopAudioOutputDeviceTest();
      closeAudioDeviceDropdowns();
      $scope.audioDeviceMenuOpen = false;
    };

    $scope.openDiagnoseModal = function() {
      $scope.closeAudioDeviceMenu();
      $modal.open({
        templateUrl: 'tpl/blocks/diagnose_connection.html',
        controller: 'DiagnoseConnectionCtrl',
        size: 'lg',
        windowClass: 'custom-rounded-modal',
        backdrop: 'static',
        resolve: {
          plivoWebSdk: function() {
            return $rootScope.plivoWebSdk;
          }
        }
      });
    };

    $scope.refreshAudioDevices = async function(forcePermission) {
      return syncAudioDevices({
        forcePermission: !!forcePermission,
        showLoading: true,
        applyToPlivo: !!$rootScope.plivoWebSdk,
        showWarnings: false,
      }).then(function() {
        return startAudioInputDeviceTestIfMenuOpen(false);
      });
    };

    function positionAudioDeviceMenu() {
      var trigger = document.querySelector('.audio-settings-trigger');
      if (!trigger) {
        $scope.audioDeviceDropdownStyle = {
          top: '80px',
          right: '8px'
        };
        return;
      }

      var rect = trigger.getBoundingClientRect();
      $scope.audioDeviceDropdownStyle = {
        top: (rect.bottom + 8) + 'px',
        right: Math.max(8, window.innerWidth - rect.right) + 'px'
      };
    }

    $scope.toggleAudioDeviceMenu = function($event) {
      if ($event) {
        $event.preventDefault();
        $event.stopPropagation();
      }
      $scope.audioDeviceMenuOpen = !$scope.audioDeviceMenuOpen;
      if ($scope.audioDeviceMenuOpen) {
        positionAudioDeviceMenu();
        $scope.refreshAudioDevices(true);
      } else {
        stopAudioInputDeviceTest();
        stopAudioOutputDeviceTest();
        closeAudioDeviceDropdowns();
      }
    };

    $scope.selectAudioInputDevice = function(deviceId) {
      $scope.pendingAudioInputDeviceId = deviceId || 'default';
      startAudioInputDeviceTest($scope.pendingAudioInputDeviceId, $scope.pendingAudioOutputDeviceId, true);
    };

    $scope.selectAudioOutputDevice = function(deviceId) {
      $scope.pendingAudioOutputDeviceId = deviceId || '';
    };

    $scope.toggleAudioInputDropdown = function($event) {
      if ($event) {
        $event.preventDefault();
        $event.stopPropagation();
      }
      $scope.audioInputDropdownOpen = !$scope.audioInputDropdownOpen;
      if ($scope.audioInputDropdownOpen) {
        $scope.audioOutputDropdownOpen = false;
      }
    };

    $scope.toggleAudioOutputDropdown = function($event) {
      if ($event) {
        $event.preventDefault();
        $event.stopPropagation();
      }
      $scope.audioOutputDropdownOpen = !$scope.audioOutputDropdownOpen;
      if ($scope.audioOutputDropdownOpen) {
        $scope.audioInputDropdownOpen = false;
      }
    };

    $scope.chooseAudioInputDevice = function(deviceId) {
      $scope.pendingAudioInputDeviceId = deviceId || 'default';
      $scope.audioInputDropdownOpen = false;
      startAudioInputDeviceTest($scope.pendingAudioInputDeviceId, $scope.pendingAudioOutputDeviceId, true);
    };

    $scope.chooseAudioOutputDevice = function(deviceId) {
      $scope.pendingAudioOutputDeviceId = deviceId || '';
      $scope.audioOutputDropdownOpen = false;
    };

    $scope.testSelectedAudioInputDevice = async function(inputDeviceId, outputDeviceId) {
      var resolvedInputDeviceId = inputDeviceId || 'default';
      if ($scope.audioInputTestActive && audioInputTestDeviceId === resolvedInputDeviceId) {
        stopAudioInputDeviceTest();
        return;
      }
      closeAudioDeviceDropdowns();
      return startAudioInputDeviceTest(resolvedInputDeviceId, outputDeviceId, true);
    };

    $scope.testSelectedAudioOutputDevice = async function(outputDeviceId, inputDeviceId) {
      var resolvedOutputDeviceId = outputDeviceId || 'default';
      var resolvedInputDeviceId = inputDeviceId || $scope.pendingAudioInputDeviceId || 'default';
      if ($scope.audioOutputTestPlaying && audioOutputTestDeviceId === resolvedOutputDeviceId) {
        stopAudioOutputDeviceTest();
        return;
      }
      closeAudioDeviceDropdowns();
      if (!supportsAudioOutputSelection()) {
        toaster.pop('warning', 'Speaker Selection Unsupported', 'This browser does not allow testing a specific output device.');
        return;
      }
      stopAudioInputDeviceTest();
      stopAudioOutputDeviceTest();
      applyAudioDevices(resolvedInputDeviceId, resolvedOutputDeviceId, true);
      try {
        if (!audioOutputTestElement) {
          audioOutputTestElement = document.getElementById('sales-audio-device-test-element');
        }
        if (!audioOutputTestElement) {
          throw new Error('Missing audio device test element');
        }
        audioOutputTestElement.pause();
        audioOutputTestElement.currentTime = 0;
        audioOutputTestElement.loop = true;
        audioOutputTestElement.volume = 0.3;
        if (audioOutputTestElement.setSinkId) {
          await audioOutputTestElement.setSinkId(resolvedOutputDeviceId === 'default' ? '' : resolvedOutputDeviceId);
        }
        await audioOutputTestElement.play();
        audioOutputTestDeviceId = resolvedOutputDeviceId;
        $scope.audioOutputTestPlaying = true;
        $scope.$applyAsync();
      } catch (error) {
        console.error('Failed to test selected speaker device', error);
        stopAudioOutputDeviceTest();
        toaster.pop('error', 'Speaker Test Failed', 'The selected output device could not play the test audio.');
      }
    };

    $scope.applyAudioDeviceSettings = function(inputDeviceId, outputDeviceId) {
      var resolvedInputDeviceId = inputDeviceId || $scope.pendingAudioInputDeviceId || 'default';
      var resolvedOutputDeviceId = outputDeviceId || $scope.pendingAudioOutputDeviceId || 'default';
      stopAudioInputDeviceTest();
      stopAudioOutputDeviceTest();
      closeAudioDeviceDropdowns();
      $scope.audioInputSelectionMode = resolvedInputDeviceId && resolvedInputDeviceId !== 'default' ? 'manual' : 'auto';
      $scope.audioOutputSelectionMode = resolvedOutputDeviceId && resolvedOutputDeviceId !== 'default' ? 'manual' : 'auto';
      $scope.selectedAudioInputDeviceId = resolvedInputDeviceId;
      $scope.selectedAudioOutputDeviceId = resolvedOutputDeviceId;
      $scope.pendingAudioInputDeviceId = resolvedInputDeviceId;
      $scope.pendingAudioOutputDeviceId = resolvedOutputDeviceId;
      applySelectedAudioDevices(true);
      if (resolvedInputDeviceId && $scope._realtimeAudioCtx && $scope._realtimeMerger) {
        swapDeepgramMicSource(resolvedInputDeviceId);
      }
      if ($scope.switchStatus) {
        startMicrophoneMonitor(true);
      }
      $scope.audioDeviceMenuOpen = false;
    };

    $scope.dismissAudioDeviceWarning = function() {
      $scope.showAudioDeviceChangedWarning = false;
      $scope.audioDeviceWarningType = '';
      $scope.audioDeviceWarningLabel = '';
      $scope.audioDeviceWarningMessage = '';
    };

    function swapDeepgramMicSource(inputDeviceId) {
      navigator.mediaDevices.getUserMedia({ audio: getAudioConstraintsForDeviceId(inputDeviceId), video: false })
        .then(function(newMicStream) {
          if ($scope._realtimeMicSource) {
            try { $scope._realtimeMicSource.disconnect(); } catch (_) {}
          }
          if ($scope._realtimeMicStream) {
            $scope._realtimeMicStream.getTracks().forEach(function(t) { t.stop(); });
          }
          if ($scope._realtimeAudioCtx && $scope._realtimeMerger) {
            var newMicSource = $scope._realtimeAudioCtx.createMediaStreamSource(newMicStream);
            newMicSource.connect($scope._realtimeMerger, 0, 0);
            $scope._realtimeMicSource = newMicSource;
            $scope._realtimeMicStream = newMicStream;
            console.log('[Realtime] Swapped Deepgram mic source to device:', inputDeviceId);
          }
        })
        .catch(function(err) {
          console.error('[Realtime] Failed to swap Deepgram mic source:', err);
        });
    }

    $scope.openAudioSettingsFromWarning = function($event) {
      if ($event) {
        $event.preventDefault();
        $event.stopPropagation();
      }
      $scope.showAudioDeviceChangedWarning = false;
      $scope.audioDeviceMenuOpen = true;
      positionAudioDeviceMenu();
      $scope.refreshAudioDevices(true);
    };

    // ─── Device Change Detection ──────────────────────────────────────────────
    // Listens for plug/unplug events and surfaces a warning banner to the user.

    if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
      audioDeviceChangeHandler = function() {
        if (audioDeviceChangeDebounce) {
          clearTimeout(audioDeviceChangeDebounce);
        }
        audioDeviceChangeDebounce = window.setTimeout(function() {
          syncAudioDevices({
            forcePermission: false,
            showLoading: !!$scope.audioDeviceMenuOpen,
            applyToPlivo: false,
            showWarnings: true,
            source: 'devicechange',
            forceRealtimeUpdate: true,
          }).then(function() {
            return startAudioInputDeviceTestIfMenuOpen(false);
          });
        }, 300);
      };
      navigator.mediaDevices.addEventListener('devicechange', audioDeviceChangeHandler);
      $scope.$on('$destroy', function() {
        if (audioDeviceChangeDebounce) {
          clearTimeout(audioDeviceChangeDebounce);
          audioDeviceChangeDebounce = null;
        }
        if (audioDeviceChangeHandler) {
          navigator.mediaDevices.removeEventListener('devicechange', audioDeviceChangeHandler);
        }
        if (combinedAudioDeviceChangeHandler && navigator.mediaDevices.ondevicechange === combinedAudioDeviceChangeHandler) {
          navigator.mediaDevices.ondevicechange = plivoAudioDeviceChangeHandler;
        }
      });
    }

    function bindPlivoAudioDeviceChangeHandler() {
      if (!(navigator.mediaDevices && audioDeviceChangeHandler)) {
        return;
      }
      var currentHandler = navigator.mediaDevices.ondevicechange;
      if (!currentHandler || currentHandler === combinedAudioDeviceChangeHandler) {
        return;
      }
      navigator.mediaDevices.removeEventListener('devicechange', audioDeviceChangeHandler);
      plivoAudioDeviceChangeHandler = currentHandler;
      combinedAudioDeviceChangeHandler = function(event) {
        plivoAudioDeviceChangeHandler.call(navigator.mediaDevices, event);
        audioDeviceChangeHandler(event);
      };
      navigator.mediaDevices.ondevicechange = combinedAudioDeviceChangeHandler;
    }

    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      $scope.refreshAudioDevices(false);
    }

    // Close audio device menu when clicking outside
    angular.element(document).on('click.audioDeviceMenu', function() {
      if ($scope.audioDeviceMenuOpen) {
        stopAudioInputDeviceTest();
        stopAudioOutputDeviceTest();
        closeAudioDeviceDropdowns();
        $scope.audioDeviceMenuOpen = false;
        $scope.$applyAsync();
      }
    });
    $scope.$on('$destroy', function() {
      stopAudioInputDeviceTest();
      stopAudioOutputDeviceTest();
      angular.element(document).off('click.audioDeviceMenu');
    });

    // ─── Microphone Permission Monitor ───────────────────────────────────────

    function monitorMicrophonePermission() {
      try {
        if (navigator && navigator.permissions && navigator.permissions.query) {
          navigator.permissions.query({ name: 'microphone' }).then(function(permissionStatus) {
            if ((permissionStatus.state === 'denied' || permissionStatus.state === 'prompt') && $scope.switchStatus) {
              applyMicrophoneCheckResult({ ok: false, reason: 'permission-denied' });
              if ($scope.turnOffSwitch) { $scope.turnOffSwitch(); }
            } else if (permissionStatus.state === 'granted' && microphoneSignals.permissionBlocked) {
              MediaHelper.checkMicrophone($scope.selectedAudioInputDeviceId).then(function(result) {
                applyMicrophoneCheckResult(result);
              });
            }
            permissionStatus.onchange = function() {
              if ((permissionStatus.state === 'denied' || permissionStatus.state === 'prompt') && $scope.switchStatus) {
                applyMicrophoneCheckResult({ ok: false, reason: 'permission-denied' });
                if ($scope.turnOffSwitch) { $scope.turnOffSwitch(); }
              } else if (permissionStatus.state === 'granted' && microphoneSignals.permissionBlocked) {
                MediaHelper.checkMicrophone($scope.selectedAudioInputDeviceId).then(function(result) {
                  applyMicrophoneCheckResult(result);
                });
              }
            };
          }).catch(function() {
            if (navigator && navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
              navigator.mediaDevices.addEventListener('devicechange', function() {
                MediaHelper.checkMicrophone($scope.selectedAudioInputDeviceId).then(function(result) {
                  applyMicrophoneCheckResult(result);
                  if (!result.ok && $scope.switchStatus && isPermissionBlockedReason(result.reason)) {
                    if ($scope.turnOffSwitch) { $scope.turnOffSwitch(); }
                  }
                });
              });
            }
          });
        } else if (navigator && navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
          navigator.mediaDevices.addEventListener('devicechange', function() {
            MediaHelper.checkMicrophone($scope.selectedAudioInputDeviceId).then(function(result) {
              applyMicrophoneCheckResult(result);
              if (!result.ok && $scope.switchStatus && isPermissionBlockedReason(result.reason)) {
                if ($scope.turnOffSwitch) { $scope.turnOffSwitch(); }
              }
            });
          });
        }
      } catch (e) {}
    }

    // ─── Public API (cross-controller access) ────────────────────────────────

    $scope.startMicrophoneMonitor = startMicrophoneMonitor;
    $scope.stopMicrophoneMonitor = stopMicrophoneMonitor;
    $scope.updateMicrophoneSignal = updateMicrophoneSignal;
    $scope.applyMicrophoneCheckResult = applyMicrophoneCheckResult;
    $scope.getMicrophoneAccessFailureMessage = getMicrophoneAccessFailureMessage;
    $scope.applySelectedAudioDevices = applySelectedAudioDevices;
    $scope.syncAudioDevices = syncAudioDevices;
    $scope.bindPlivoAudioDeviceChangeHandler = bindPlivoAudioDeviceChangeHandler;
    $scope.applyAudioOutputSinkToRemoteAudio = applyAudioOutputSinkToRemoteAudio;
    $scope.monitorMicrophonePermission = monitorMicrophonePermission;

  }
]);
